import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Fills the cart drawer's recommendations rail on first open (SPEC §3.7).
 *
 * The one rule this exists to keep: **no request during page load.** The rail
 * ships empty and this fetches it once, the first time the drawer actually has a
 * box on screen. QA-07 checks that in the Network panel, so the trigger is
 * visibility rather than any of the several ways the vendor drawer can be
 * opened.
 *
 * The rail is the compatible products of the cart's first three products, so it
 * is a function of the cart: it is fetched again when the cart changes — at once
 * if the drawer is open (a product added from the rail itself leaves it), on the
 * next open otherwise. Reopening an unchanged cart does not refetch, because a
 * rail that reshuffles on its own reads as instability, not as freshness.
 *
 * @extends {Component<{ body: HTMLElement }>}
 */
class AdmiraCartRecommendations extends Component {
  requiredRefs = ['body'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** True when the rail on screen is out of date: never fetched, or the cart changed since. */
  #stale = true;

  /** Bumped per request so a slow, superseded response cannot overwrite a newer one. */
  #request = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        // Deliberately still observing: a failed fetch or a later cart change
        // leaves `#stale` set, so the next time the drawer opens it tries again.
        if (this.#stale) this.#load();
      }
    });

    // The drawer's dialog, not the rail: the rail is `display: none` until it
    // has a list, and a box that is never rendered never intersects — observing
    // it meant the fetch that would fill it could never start.
    this.#observer.observe(this.closest('dialog') ?? this);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /**
   * The cart changed, so what the rail shows may no longer fit it. Waits for the
   * cart request to settle: the section render reads the cart as it is then.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onCartUpdate = (event) => {
    this.#stale = true;

    event.promise
      ?.then(() => {
        // Closed: the next open refetches, and a fetch nobody sees is a wasted one.
        if (this.closest('dialog')?.open) this.#load();
      })
      .catch(() => {});
  };

  async #load() {
    this.#stale = false;
    const request = ++this.#request;

    const { body } = this.refs;
    const sectionId = this.dataset.sectionId ?? 'admira-cart-recommendations';

    body.setAttribute('aria-busy', 'true');

    try {
      const endpoint = new URL(window.location.pathname, window.location.origin);
      endpoint.searchParams.set('section_id', sectionId);

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(String(response.status));

      const html = await response.text();
      if (request !== this.#request) return;
      body.innerHTML = html;
    } catch {
      if (request !== this.#request) return;

      // Nothing to show is the rail's documented empty state, and the CSS
      // already collapses it. An error message beside someone's cart is worse
      // than no panel.
      body.replaceChildren();

      // Allow one more attempt on the next open: a single failed request should
      // not disable the rail for the rest of the session.
      this.#stale = true;
    } finally {
      if (request === this.#request) body.setAttribute('aria-busy', 'false');
    }
  }
}

if (!customElements.get('admira-cart-recommendations')) {
  customElements.define('admira-cart-recommendations', AdmiraCartRecommendations);
}
